<svelte:options runes={false} />
<script>
  // Errors tab (plan 2026-06-14-002, U6 / R3): the strategy's durable error history — a timestamped
  // Time/Severity/Code/Message table, newest-first, that survives reloads/restarts. The authoritative history
  // comes from the membership-gated, org-scoped REST read (getStrategyErrors); new errors live-APPEND from the
  // autotradeAlerts SSE store (filtered to this strategy + severity >= Error), DEDUPED by `seq` so the same row
  // arriving via both REST seed and SSE catch-up shows once. Distinct from LiveStatusSurface (KTD7 — additive,
  // no cross-suppression): that strip shows ALL alerts compactly; this is the durable ≥Error log.
  import { onDestroy } from 'svelte';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { strategyErrors, reloadStrategyErrors, fmtErrorTime as fmtTime } from '../../../lib/strategyErrors.js';

  export let strategy = null;
  export let status = null;

  $: strategyId = strategy?.strategyId ?? null;
  $: isRunning = String(status) === 'Running';

  // The shared, deduped, severity-gated, newest-first errors source (REST history + live SSE). The shared
  // loader fetches once per (strategy, org); the badge + banner consume the same source.
  $: merged = $errorsStore;
  let errorsStore = strategyErrors(null, null);
  let loading = false;
  let error = null;

  // (Re)bind the shared store and refresh on strategy/org change. The refresh surfaces a tab-level error state
  // (the shared store swallows to empty; the explicit reload rejects so we can show "couldn't load").
  let lastKey = '';
  $: {
    const key = `${strategyId}|${$selectedOrgId}`;
    if (key !== lastKey && strategyId) {
      lastKey = key;
      errorsStore = strategyErrors(strategyId, $selectedOrgId);
      void refresh();
    }
  }

  async function refresh() {
    if (!strategyId) return;
    loading = true;
    error = null;
    const at = $selectedOrgId;
    try {
      await reloadStrategyErrors(strategyId, at);
    } catch (err) {
      if ($selectedOrgId === at && strategy?.strategyId === strategyId) error = mapError(err);
    } finally {
      loading = false;
    }
  }

  function mapError(err) {
    const msg = String(err?.message ?? err);
    if (msg.includes('status: 404')) return null; // unreadable/absent — treated as empty, not an error banner
    return "Couldn't load error history — try reloading.";
  }

  // Fallback liveness poll for a running strategy (the SSE append is best-effort; an SSE drop or a slow-cadence
  // strategy can leave gaps) — mirrors CandlesSignalsTab's decoupled poll.
  const FALLBACK_POLL_MS = 30000;
  let pollTimer = null;
  $: if (isRunning && !pollTimer) {
    pollTimer = setInterval(refresh, FALLBACK_POLL_MS);
  } else if (!isRunning && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });
</script>

<div class="at-errors-tab">
  <div class="at-errors-toolbar">
    {#if isRunning}
      <span class="at-live-badge" title="Updating live as errors occur">● live</span>
    {/if}
    <button type="button" class="btn btn-ghost btn-sm" on:click={refresh} disabled={loading}>Refresh</button>
  </div>

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={refresh}>Retry</button>
    </div>
  {:else if loading && merged.length === 0}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if merged.length === 0}
    <div class="empty-state">
      <p class="empty-state-description">No errors recorded for this strategy.</p>
    </div>
  {:else}
    <div class="at-debug-table">
      <div class="at-debug-scroll">
        <table>
          <thead>
            <tr><th>Time</th><th>Severity</th><th>Code</th><th>Message</th></tr>
          </thead>
          <tbody>
            {#each merged as e (e.seq)}
              <tr>
                <td>{fmtTime(e.time)}</td>
                <td class="at-alert at-sev-{String(e.severity).toLowerCase()}">{e.severity}</td>
                <td>{e.code}</td>
                <td>{e.message}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

<style>
  /* Skeleton/empty/alert/btn classes are global (autotrading.css is imported app-wide in main.js); only the
     errors-table + severity styles are scoped here. */
  .at-errors-toolbar { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
  .at-live-badge { font-size: 0.75rem; color: var(--success, #1aa260); }
  .at-debug-table { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; }
  .at-debug-scroll { max-height: 480px; overflow: auto; }
  .at-debug-table table { width: 100%; border-collapse: collapse; font-size: 0.78rem; }
  .at-debug-table th, .at-debug-table td { padding: 0.4rem 0.6rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); text-align: left; vertical-align: top; }
  .at-debug-table th { position: sticky; top: 0; background: var(--surface-1, #14151a); }
  .at-debug-table td:last-child { white-space: normal; word-break: break-word; }
  .at-sev-error, .at-sev-critical { color: var(--danger, #e0533a); font-weight: 700; }
</style>
